/** One trip of the Trips panel (plan "Page anatomy" → Trip row): two lines, the whole row a
 *  link to its booking (cmd-clickable — the tour name's link stretches over the row),
 *  tabular numerals.
 *
 *  When · Trip · Where · With · App & chat · row menu. The guide-app and chat lines are
 *  the bookings grid's own words (`tripStatus`, `TripStatusLine`), icon and words, the
 *  evidence in the tooltip; a guest booking shows the chat line only (guests are not
 *  windowed). Below 1000 px of content the row stacks (When + Trip / Where / With /
 *  status), below 600 px one column. */
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Chip, IconButton, Link, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { resyncChat } from '@/api/bookingActions';
import type { BookingListItem } from '@/api/bookings';
import { BookingStateChip } from '@/features/bookings/BookingStateChip';
import { CACHE_PERM, ChatResyncPopover } from '@/features/bookings/record/useBookingCacheActions';
import { PARTICIPANT_LABEL } from '@/features/bookings/tripCells';
import { integrationName, routeText, startPlaceText, TripStatusLine } from '@/features/bookings/tripExpansionCells';
import { chatStatus, guideAppStatus } from '@/features/bookings/tripStatus';
import { usePermissions } from '@/hooks/usePermissions';

import { BOOKED_STATE, isStaffRow, tripMeta, tripWhen, withText } from './tripsModel';

export type TripRowLayout = 'wide' | 'stacked' | 'narrow';

/** The row's layout for the panel's content width (0 = unmeasured: wide). */
export function tripRowLayout(width: number): TripRowLayout {
  if (width === 0 || width >= 1000) return 'wide';
  return width >= 600 ? 'stacked' : 'narrow';
}

const GRID: Record<TripRowLayout, Record<string, string>> = {
  wide: {
    gridTemplateColumns:
      'minmax(150px, 1.1fr) minmax(170px, 1.3fr) minmax(150px, 1.2fr) minmax(150px, 1.1fr) minmax(200px, 1.3fr) 32px',
    gridTemplateAreas: '"when trip where with status menu"',
  },
  stacked: {
    gridTemplateColumns: 'minmax(150px, 0.8fr) minmax(0, 1.6fr) 32px',
    gridTemplateAreas: '"when trip menu" "where where menu" "with with menu" "status status menu"',
  },
  narrow: {
    gridTemplateColumns: 'minmax(0, 1fr) 32px',
    gridTemplateAreas: '"when menu" "trip menu" "where menu" "with menu" "status menu"',
  },
};

/** Above the row's stretched link: what has its own pointer behaviour (tooltips, chips, the
 *  menu). */
const RAISED = { position: 'relative', zIndex: 1 } as const;

function Cell({ area, main, sub, raised = false }: { area: string; main: ReactNode; sub?: ReactNode; raised?: boolean }) {
  return (
    <Box sx={{ gridArea: area, minWidth: 0, ...(raised ? RAISED : {}) }} data-trip-cell={area}>
      <Box sx={{ typography: 'body2', minWidth: 0, overflowWrap: 'anywhere' }}>{main}</Box>
      {sub && (
        <Typography component="div" variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

const Muted = ({ children }: { children: ReactNode }) => (
  <Box component="span" sx={{ color: 'text.secondary' }}>
    {children}
  </Box>
);

export function TripRow({
  row,
  axisZone,
  now,
  layout,
}: {
  row: BookingListItem;
  axisZone: string;
  now: Date;
  layout: TripRowLayout;
}) {
  const when = tripWhen(row, axisZone, now);
  const staff = isStaffRow(row);
  const href = `/bookings/${row.id}`;

  // Where: the start place and the route (the grid's Start / Route columns).
  const w = row.where;
  const start = startPlaceText(row);
  const route = routeText(row);
  const whereMain = w ? start || <Muted>No start place</Muted> : <Muted>—</Muted>;
  const whereSub = w
    ? route || `No route${row.integration ? ` from ${integrationName(row.integration)}` : ''}`
    : null;

  // With: the guides of a guest's departure; a staff booking's role, guests and co-staff.
  const party = !staff && row.people_count ? `party of ${row.people_count}` : null;
  const withMain = withText(row);
  const assigned = row.guides?.assigned.map((a) => `${a.category?.name ?? 'Assigned'}: ${a.tag.name}`).join(' · ');
  const roleChip =
    staff || row.participant_kind === 'observer' ? (
      <Tooltip title={row.role?.name ?? PARTICIPANT_LABEL[row.participant_kind]} placement="top">
        <Chip
          size="small"
          variant="outlined"
          label={PARTICIPANT_LABEL[row.participant_kind]}
          sx={{ height: 20, mr: 0.75, fontSize: 12, ...RAISED }}
        />
      </Tooltip>
    ) : null;

  return (
    <Box
      role="listitem"
      data-trip-row={row.id}
      data-layout={layout}
      sx={{
        position: 'relative',
        display: 'grid',
        ...GRID[layout],
        columnGap: 2,
        rowGap: 0.5,
        alignItems: 'start',
        px: 1,
        py: 1,
        minHeight: 60,
        borderTop: 1,
        borderColor: 'divider',
        fontVariantNumeric: 'tabular-nums',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Cell area="when" main={when.main} sub={when.sub.join(' · ') || null} />

      <Box sx={{ gridArea: 'trip', minWidth: 0 }} data-trip-cell="trip">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Link
            id={`trip-${row.id}`}
            component={RouterLink}
            to={href}
            underline="hover"
            color="text.primary"
            sx={{
              typography: 'body2',
              fontWeight: 600,
              overflowWrap: 'anywhere',
              '&::after': { content: '""', position: 'absolute', inset: 0, borderRadius: 1 },
              '&:focus-visible': { outline: 'none' },
              '&:focus-visible::after': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
            }}
          >
            {row.tour.name}
          </Link>
          {row.booking_state !== BOOKED_STATE && (
            <Box component="span" sx={RAISED}>
              <BookingStateChip booking_state={row.booking_state} booking_state_display={row.booking_state_display} />
            </Box>
          )}
        </Box>
        <Typography component="div" variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {tripMeta(row) || '—'}
        </Typography>
      </Box>

      <Cell area="where" main={whereMain} sub={whereSub} />

      <Cell
        area="with"
        main={
          <>
            {roleChip}
            {[withMain, party].filter(Boolean).join(' · ') || <Muted>{staff ? 'No guests listed' : 'No guides listed'}</Muted>}
          </>
        }
        sub={assigned || null}
      />

      <Box sx={{ gridArea: 'status', minWidth: 0, display: 'grid', gap: 0.25, ...RAISED, justifySelf: 'start', maxWidth: '100%' }} data-trip-cell="status">
        {staff && row.staff_visibility && (
          <Box sx={{ typography: 'body2' }} data-status-line="app">
            <TripStatusLine status={guideAppStatus(row.staff_visibility, row.timezone, now)} />
          </Box>
        )}
        {row.chat && (
          <Box sx={{ typography: 'body2' }} data-status-line="chat">
            <TripStatusLine status={chatStatus(row.chat, row.timezone)} />
          </Box>
        )}
      </Box>

      <Box sx={{ gridArea: 'menu', ...RAISED }}>
        <RowMenu bookingId={row.id} />
      </Box>
    </Box>
  );
}

/** The booking page's own gates: its route (and so its Cache tab) needs
 *  console_bookings_view; Resync chat membership needs console_bookings_edit (`CACHE_PERM`). */
const BOOKING_VIEW_PERM = 'console_bookings_view';

/** The row's menu, gated as the booking page gates the same things: the booking, its Cache
 *  tab (is it in the guide's cache, and why), and Resync chat — the booking's own action and
 *  confirmation, which the "Not placed in chat" alert points to. What the operator may not
 *  do is left out. */
function RowMenu({ bookingId }: { bookingId: number }) {
  const { hasPerm } = usePermissions();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [resyncAnchor, setResyncAnchor] = useState<HTMLElement | null>(null);
  const canView = hasPerm(BOOKING_VIEW_PERM);
  const canResync = hasPerm(CACHE_PERM.edit);
  if (!canView && !canResync) return null;
  return (
    <>
      <IconButton
        size="small"
        aria-label={`Actions for booking ${bookingId}`}
        aria-haspopup="menu"
        aria-expanded={anchor ? true : undefined}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {canView && (
          <MenuItem component={RouterLink} to={`/bookings/${bookingId}`} onClick={() => setAnchor(null)}>
            Open booking
          </MenuItem>
        )}
        {canView && (
          <MenuItem component={RouterLink} to={`/bookings/${bookingId}/cache`} onClick={() => setAnchor(null)}>
            Cache verdict
          </MenuItem>
        )}
        {canResync && (
          <MenuItem
            onClick={() => {
              // The confirmation hangs on the row's menu button, as the booking's does on its
              // Actions button.
              setResyncAnchor(anchor);
              setAnchor(null);
            }}
            data-row-action="chat_resync"
          >
            Resync chat…
          </MenuItem>
        )}
      </Menu>
      {resyncAnchor && (
        <ChatResyncPopover anchorEl={resyncAnchor} onClose={() => setResyncAnchor(null)} run={() => resyncChat(bookingId)} />
      )}
    </>
  );
}
